import { ReactNode, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  Table,
  useReactTable
} from "@tanstack/react-table";

import {
  CloseIcon,
  DashboardIcon,
  LookupIcon,
  MakerIcon,
  MapIcon
} from "@/assets/icons";
import TableData from "@/components/dataset/datasetTable/TableData";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import Pagination from "@/components/datasetManagement/Pagination";
import TabSelect, { TabItem } from "@/components/tabSelect";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import MultipleSearchWithSelect from "@/components/ui/MultipleSearchWithSelect";
import { NoDataMessage } from "@/components/ui/NoDataMessage";
import SearchWithSelect, {
  SearchFilterParams
} from "@/components/ui/searchWithSelect";
import { DATA_TYPE_VALUES } from "@/constants/dataset";
import { normalizeFilterValue } from "@/lib/utils";
import { RootState } from "@/store";
import { clearSelectedDataset } from "@/store/slice/datasetSlice";
import { toggleSpatialAnalysisResultTabularModal } from "@/store/slice/uiSlice";
import { DatasetDataType } from "@/types/dataset";
import { LayerType } from "@/types/map";
import {
  generateFilterableAttributes,
  getAvailableDatasets
} from "@/utils/dataset";
import {
  formatDatasetRows,
  generateColumnFilters,
  getTableState,
  includesStringFilter
} from "@/utils/table";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { generateColumns, SpatialAnalysisRow } from "./columns";
import { DATASETS_RESULTS_ANALYSIS_NO_DATA_MESSAGE_MAPPING } from "./data";
import { usePreviewGeom } from "@/hooks/map/usePreviewGeom";

type SpatialInfo = {
  [key: string]: {
    icon: ReactNode;
    label: string;
    textColor: string;
  };
};

export interface SearchFilterState {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
}

type RowData = Record<string, string | number | boolean | null>;

const POLYGON_INFO = {
  icon: <DashboardIcon className="fill-tangerine w-[12px]" />,
  label: "Polygon",
  textColor: "text-tangerine"
};
const GRID_INFO = {
  icon: <LookupIcon className="fill-teal-500 size-3" />,
  label: "Grid",
  textColor: "text-blue-violet"
};
const PROFILING_INFO = {
  icon: <LookupIcon className="fill-teal-500 size-3" />,
  label: "Profiling",
  textColor: "text-blue-violet"
};

const SPATIAL_INFO: SpatialInfo = {
  [LayerType.ADMINISTRATIVE]: {
    icon: <MapIcon className="fill-royal-blue w-[12px]" />,
    label: "Admin. Area",
    textColor: "text-royal-blue"
  },
  [LayerType.POLYGON]: { ...POLYGON_INFO },
  [LayerType.H3]: { ...POLYGON_INFO },
  [LayerType.GEOHASH]: { ...POLYGON_INFO },
  [LayerType.POINT]: {
    icon: <MakerIcon className="fill-blue-violet w-[12px]" />,
    label: "Point",
    textColor: "text-blue-violet"
  },
  [LayerType.GRID]: { ...GRID_INFO },
  [LayerType.PROFILING]: { ...PROFILING_INFO }
};

type SpatialAnalysisTabularModalProps = Readonly<{
  isMultipleSearchTableDataset?: boolean;
  selectedAttribute?: SearchFilterState | null;
}>;

export default function SpatialAnalysisTabularModal({
  isMultipleSearchTableDataset = false,
  selectedAttribute = null
}: SpatialAnalysisTabularModalProps) {
  const dispatch = useDispatch();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { showSpatialAnalysisResultTabularModal } = useSelector(
    (state: RootState) => state.ui
  );

  const { selectedDataset, datasets, visualizationOrder, rawDatasets } =
    useSelector((state: RootState) => state.dataset);

  const [selectedTab, setSelectedTab] = useState(
    selectedDataset?.data.id || ""
  );

  const columns = rawDatasets?.[selectedTab]?.columns;

  const defaultFilter = generateFilterableAttributes(columns);
  const firstType = defaultFilter[0]?.type ?? null;
  const firstColumn = defaultFilter[0]?.value ?? null;

  const initialFilterState: SearchFilterState = selectedAttribute ?? {
    query: null,
    query_type: firstType ? [firstType] : null,
    query_column: firstColumn ? [firstColumn] : null
  };

  const [filterState, setFilterState] =
    useState<SearchFilterState>(initialFilterState);

  const availableDatasets = getAvailableDatasets(datasets);
  const { showComparisonPopup } = useSelector((state: RootState) => state.ui);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleClose = () => {
    if (!showComparisonPopup.isShow) dispatch(clearSelectedDataset());
    dispatch(toggleSpatialAnalysisResultTabularModal());
  };

  const tabs = visualizationOrder
    .filter((datasetId) =>
      Object.prototype.hasOwnProperty.call(availableDatasets, datasetId)
    )
    .map((datasetId) => ({
      label: availableDatasets[datasetId]?.data.name,
      value: datasetId,
      icon: SPATIAL_INFO[availableDatasets[datasetId].properties.spatialType]
        .icon
    })) as TabItem[];

  const rows = useMemo(() => {
    return rawDatasets?.[selectedTab]?.rows || [];
  }, [rawDatasets, selectedTab]);

  const dynamicColumns = useMemo(() => {
    return (
      columns?.map((column) => ({
        attribute_name: column.name,
        data_type: DATA_TYPE_VALUES[column.data_type]
      })) || []
    );
  }, [columns]);

  const handleTabChange = (tabValue: string) => {
    const columns = rawDatasets?.[tabValue]?.columns;

    setFilterState({
      query: null,
      query_type: generateFilterableAttributes(columns)[0]?.type
        ? [generateFilterableAttributes(columns)[0].type]
        : null,
      query_column: generateFilterableAttributes(columns)[0]?.value
        ? [generateFilterableAttributes(columns)[0].value]
        : null
    });
    setSelectedTab(tabValue);

    // Reset pagination and sorting when tab changes
    setCurrentPage(1);
    setSorting([]);
  };

  const data: RowData[] = useMemo(() => {
    if (!columns || columns.length === 0) {
      return [];
    }

    return rows.map((row) =>
      row.reduce<RowData>((acc, valueItem, index) => {
        if (columns[index] && valueItem !== null && valueItem !== undefined) {
          acc[normalizeFilterValue(columns[index].name)] = valueItem;
        }
        return acc;
      }, {})
    );
  }, [rows, columns]);

  const columnFilters = useMemo(() => {
    return generateColumnFilters(filterState);
  }, [filterState]);

  const formattedData = useMemo(() => {
    return formatDatasetRows(data, dynamicColumns);
  }, [data, dynamicColumns]);

  const { handlePreviewClick, getPreviewDataFromRow } = usePreviewGeom({
    handleClose,
    selectedDataset: selectedTab
  });

  const baseColumns = generateColumns(
    dynamicColumns,
    isMultipleSearchTableDataset,
    handlePreviewClick,
    getPreviewDataFromRow
  );

  const table = useReactTable<SpatialAnalysisRow>({
    data: formattedData,
    columns: baseColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
        pageIndex: currentPage - 1
      },
      columnPinning: {
        right: ["action"]
      }
    },
    onSortingChange: (sorting) => {
      setSorting(sorting);
      setCurrentPage(1);
    },
    getSortedRowModel: getSortedRowModel(),
    filterFns: {
      includesString: includesStringFilter
    },
    state: getTableState(currentPage, pageSize, sorting, columnFilters)
  });

  const handleSingleFilterChange = (params: SearchFilterParams) => {
    const normalizedParams: SearchFilterState = {
      query: params.query ? [params.query] : null,
      query_type: params.query_type ? [params.query_type] : null,
      query_column: params.query_column ? [params.query_column] : null
    };

    setFilterState(normalizedParams);
    setCurrentPage(1);
  };

  const messageMapping = (rows?.length ?? 0) <= 0 ? "no-data" : "no-result";

  return (
    <Dialog
      open={showSpatialAnalysisResultTabularModal}
      onOpenChange={handleClose}
    >
      <VisuallyHidden asChild>
        <DialogTitle>Spatial Analysis Data Table</DialogTitle>
      </VisuallyHidden>
      <DialogContent
        className="flex flex-col p-8 max-w-[calc(100vw-50px)] max-h-[calc(100vh-50px)] w-full h-full overflow-hidden"
        isHideClose
      >
        <div>
          <div className="flex flex-row items-start justify-between">
            <h1
              className="mb-1 text-xl font-semibold text-txtHeading"
              data-testid="modal-title"
            >
              Data Table
            </h1>
            <button
              className="p-0"
              onClick={handleClose}
              data-testid="close-button"
              aria-label="Close modal"
              type="button"
            >
              <CloseIcon focusable="false" />
            </button>
          </div>

          <TabSelect
            className="h-[32px] overflow-x-auto overflow-y-hidden scrollbar-overlay"
            buttonClassName="text-xs h-[24px]"
            tabList={tabs}
            variant="underline"
            selected={selectedTab}
            onSelect={handleTabChange}
          />

          <p className="mt-2 text-sm text-txtBody">
            {availableDatasets[selectedTab]?.data.short_description ?? ""}
          </p>
        </div>
        {!isMultipleSearchTableDataset && (
          <SearchWithSelect<SearchFilterParams>
            searchParams={(() => {
              const state = filterState;
              return {
                query: state.query?.[0] ?? null,
                query_type: state.query_type?.[0] ?? null,
                query_column: state.query_column?.[0] ?? null
              };
            })()}
            options={generateFilterableAttributes(columns)}
            onParamsChange={handleSingleFilterChange}
            disabled={(rows.length ?? 0) <= 0}
          />
        )}
        {isMultipleSearchTableDataset && (
          <MultipleSearchWithSelect
            options={generateFilterableAttributes(columns ?? [])}
            disabled={(rows?.length ?? 0) <= 0}
            onParamsChange={(params) => {
              setFilterState(params);
              setCurrentPage(1);
            }}
            searchParams={filterState}
          />
        )}
        <TableData<SpatialAnalysisRow>
          table={table}
          emptyDataMessage={
            <NoDataMessage
              message={
                DATASETS_RESULTS_ANALYSIS_NO_DATA_MESSAGE_MAPPING[
                  messageMapping
                ]
              }
            />
          }
        />

        <Pagination
          table={table as unknown as Table<DatasetDetailRow>}
          pageSize={pageSize}
          setPageSize={handlePageSizeChange}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalData={table.getFilteredRowModel().rows.length}
        />
      </DialogContent>
    </Dialog>
  );
}
